/**
 * MVP module — cost category scorer (Phase B milestone B2).
 * Policy anchors: MVP guide §3, Trad guide §3.3–3.5, delta doc §1.2.
 *
 * Rules:
 *  - pool = pathway costMeasureIds ∩ scoreable prior-year scores (case min met,
 *    points present). Traditional pathway = ALL scoreable measures (no restriction).
 *  - denominator counts ONLY scored measures (an unscoreable pool measure is
 *    excluded from numerator AND denominator — never drags the score down)
 *  - empty pool → the category is REWEIGHTED to 0% (signaled to the weight resolver)
 *  - improvement: achievement percentages, ((cur − prior)/prior) expressed in
 *    percentage points, floor 0, cap 1pp (Trad §3.4 — the /100 form)
 *  - v1 reuses prior-year decile points; costPointsFromDollars implements the
 *    SD-band table for v2 re-scoring of raw $ values (Trad §3.3–3.4)
 */
import type { ParameterSet } from './parameters/py2026';
import type { CostMeasureScore, PriorYearInputs } from './types';

export interface MvpCostResult {
  /** null = no scoreable measure in the pool → category reweighted to 0%. */
  pct: number | null;
  achievementPct: number | null;
  reweighted: boolean;
  detail: {
    poolSize: number;
    scored: Array<{ measureId: string; points: number }>;
    excludedNotScoreable: string[];   // in pool, but case min failed / no points
    improvementPP: number;
  };
}

export function scoreMvpCost(args: {
  costScores: CostMeasureScore[];
  /** Pathway's costMeasureIds; null = Traditional (all scoreable measures count). */
  costPool: string[] | null;
  priorYear: PriorYearInputs;
  params: ParameterSet;
}): MvpCostResult {
  const { costScores, costPool, priorYear, params } = args;

  const inPool = costPool == null
    ? costScores
    : costScores.filter(c => costPool.includes(c.measureId));
  const scored = inPool.filter(c => c.caseMinMet && c.points != null);
  const excluded = inPool.filter(c => !(c.caseMinMet && c.points != null)).map(c => c.measureId);

  if (scored.length === 0) {
    return {
      pct: null,
      achievementPct: null,
      reweighted: true,
      detail: {
        poolSize: costPool == null ? costScores.length : costPool.length,
        scored: [],
        excludedNotScoreable: excluded,
        improvementPP: 0,
      },
    };
  }

  const points = scored.reduce((sum, c) => sum + (c.points as number), 0);
  const achievementPct = (points / (scored.length * params.pointsPerMeasureMax)) * 100;

  let improvementPP = 0;
  if (priorYear.costAchievementPct != null && priorYear.costAchievementPct > 0 && priorYear.sameIdentifier) {
    // ((cur − prior) / prior) read directly as percentage points (Trad §3.4:
    // (70%−60%)/60% ⇒ 0.17pp), floor 0, cap 1pp.
    improvementPP = (achievementPct - priorYear.costAchievementPct) / priorYear.costAchievementPct;
    improvementPP = Math.max(0, Math.min(params.costImprovementCapPP, improvementPP));
  }

  return {
    pct: Math.min(100, achievementPct + improvementPP),
    achievementPct,
    reweighted: false,
    detail: {
      poolSize: costPool == null ? costScores.length : costPool.length,
      scored: scored.map(c => ({ measureId: c.measureId, points: c.points as number })),
      excludedNotScoreable: excluded,
      improvementPP,
    },
  };
}

/**
 * SD-band cost scoring (Trad §3.3–3.4) — converts a raw $ figure into 1-10
 * achievement points against the measure's median + standard deviation.
 * Bands 1→10 bounded at median + [2.75, 2.5, 2.25, 2, 1.5, 1, 0.5, −0.5, −1, −1.5]σ;
 * LOWER cost = MORE points; within-band linear interpolation
 * (points = band + (cost − bandBottom)/(bandTop − bandBottom), where bottom is the
 * higher-cost end). Costs above median+2.75σ floor at 1; at/below median−1.5σ = 10.
 * v2 path — not called by the v1 engine (which reuses prior decile points).
 */
export function costPointsFromDollars(
  cost: number,
  median: number,
  sd: number,
  params: ParameterSet
): number {
  const cutoffs = params.costBandCutoffsSD.map(k => median + k * sd); // bands 1..10 boundaries (worst-cost end)
  if (cost >= cutoffs[0]) return 1;                    // at/above the band-1 boundary → floor
  if (cost <= cutoffs[9]) return 10;                   // at/below the band-10 boundary → max

  // Find the band whose [boundary(band), boundary(band+1)) contains the cost.
  for (let band = 1; band <= 9; band++) {
    const bottom = cutoffs[band - 1];                  // higher cost (worse)
    const top = cutoffs[band];                         // lower cost (better)
    if (cost <= bottom && cost > top) {
      const partial = (cost - bottom) / (top - bottom); // 0 at bottom → 1 at top
      return band + partial;
    }
  }
  return 1; // unreachable
}
